import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { setAuthToken, setUser } from '../utils/auth';
import { LogIn } from 'lucide-react';
import { toast } from 'sonner';

const Auth = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const loadingToast = toast.loading('Signing in...');

    try {
      const response = await api.post('/auth/login', {
        email: formData.email,
        password: formData.password,
      });
      
      setAuthToken(response.data.token);
      setUser(response.data.user);
      
      toast.dismiss(loadingToast);
      toast.success(`Welcome ${response.data.user.name}!`, {
        duration: 2000,
      });
      
      // Navigate based on role
      setTimeout(() => {
        const role = response.data.user.role;
        if (role === 'super_admin') {
          navigate('/super-admin/dashboard');
        } else if (role === 'school_admin') {
          navigate('/school-admin/dashboard');
        } else if (role === 'admin') {
          // Legacy support - redirect to super admin
          navigate('/super-admin/dashboard');
        } else if (role === 'teacher') {
          navigate('/teacher/dashboard');
        } else if (role === 'data_entry_operator') {
          navigate('/data-entry/dashboard');
        } else if (role === 'question_bank_manager') {
          navigate('/qbm/dashboard');
        } else if (role === 'blog_author') {
          navigate('/blog-author/dashboard');
        } else {
          navigate('/student/dashboard');
        }
      }, 800);
    } catch (err) {
      toast.dismiss(loadingToast);
      const errorMsg = err.response?.data?.detail || 'Login failed';
      setError(errorMsg);
      toast.error(errorMsg, {
        duration: 4000,
      });
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left Side - Hero Image */}
      <div 
        className="hidden lg:block lg:w-1/2 bg-cover bg-center relative"
        style={{ backgroundImage: 'url(https://images.pexels.com/photos/11200121/pexels-photo-11200121.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)' }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/60 to-purple-900/60 flex items-center justify-center">
          <div className="text-white text-center p-8">
            <h1 className="text-5xl font-bold mb-4">MyExamDesk</h1>
            <p className="text-xl">The smart examination platform for modern schools</p>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h2 className="text-4xl font-bold text-slate-900 mb-2">Welcome Back</h2>
            <p className="text-slate-600">Sign in to your account</p>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-8 shadow-sm">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  data-testid="auth-email-input"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  placeholder="you@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Password
                </label>
                <input
                  type="password"
                  data-testid="auth-password-input"
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full h-11 px-4 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  placeholder="********"
                />
              </div>

              {error && (
                <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                  {error}
                </div>
              )}

              <button
                type="submit"
                data-testid="auth-submit-btn"
                disabled={loading}
                className="w-full h-11 bg-indigo-700 hover:bg-indigo-800 text-white font-medium rounded-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? (
                  'Please wait...'
                ) : (
                  <>
                    <LogIn className="w-5 h-5" />
                    Sign In
                  </>
                )}
              </button>
            </form>

            <div className="mt-6 text-center text-sm text-slate-500 space-y-2">
              <p>
                <button
                  type="button"
                  onClick={() => navigate('/forgot-password')}
                  className="text-indigo-600 hover:underline font-medium"
                >
                  Forgot your password?
                </button>
              </p>
              <p>
                Don't have an account?{' '}
                <button
                  type="button"
                  onClick={() => navigate('/contact')}
                  className="text-indigo-600 hover:underline font-medium"
                >
                  Contact us
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Auth;
